import Fuse from 'fuse.js';

// Pomocnicze
function escapeHtml(str) {
  return str.replace(/[&<>"']/g, (m) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }[m]));
}

function highlightMatch(text, indices) {
  if (!Array.isArray(indices) || indices.length === 0) {
    return escapeHtml(text);
  }

  // Fuse może zwracać zakresy jako [start, end] albo { start, end } (inclusive)
  const ranges = indices
    .map((pair) => {
      if (Array.isArray(pair)) return [Number(pair[0]), Number(pair[1])];
      if (pair && typeof pair === 'object') return [Number(pair.start), Number(pair.end)];
      return [NaN, NaN];
    })
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && s <= e)
    .sort((a, b) => a[0] - b[0]);

  let out = '';
  let last = 0;

  for (const [start, end] of ranges) {
    out += escapeHtml(text.slice(last, start));
    out += `<mark>${escapeHtml(text.slice(start, end + 1))}</mark>`;
    last = end + 1;
  }

  out += escapeHtml(text.slice(last));
  return out;
}

function highlightQuery(text, query) {
  const q = (query || '').trim();
  if (!q) return escapeHtml(text);

  const hay = text;
  const hayLower = hay.toLowerCase();
  const qLower = q.toLowerCase();

  let out = '';
  let from = 0;

  while (true) {
    const idx = hayLower.indexOf(qLower, from);
    if (idx === -1) break;

    out += escapeHtml(hay.slice(from, idx));
    out += `<mark>${escapeHtml(hay.slice(idx, idx + q.length))}</mark>`;
    from = idx + q.length;
  }

  out += escapeHtml(hay.slice(from));
  return out;
}

function disableAccordionParent(data) {
  if (data.parentDisabled) return;

  data.collapseEls.forEach((el) => {
    // zapamiętaj pierwotną wartość
    if (!data.bsParentMap.has(el)) {
      data.bsParentMap.set(el, el.getAttribute('data-bs-parent'));
    }

    // usuń wymuszenie akordeonu (tylko jeden otwarty)
    el.removeAttribute('data-bs-parent');

    // przeładuj instancję Collapse, żeby pozbyć się ustawionego parent w instancji
    const inst = window.bootstrap.Collapse.getInstance(el);
    if (inst) inst.dispose();
  });

  data.parentDisabled = true;
}

function restoreAccordionParent(data) {
  if (!data.parentDisabled) return;

  data.collapseEls.forEach((el) => {
    const original = data.bsParentMap.get(el);
    if (original) {
      el.setAttribute('data-bs-parent', original);
    } else {
      el.removeAttribute('data-bs-parent');
    }

    const inst = window.bootstrap.Collapse.getInstance(el);
    if (inst) inst.dispose();
  });

  data.parentDisabled = false;
}

export function resetFilterSearch(e) {
  if (!e.target.data) {
    const key = e.target.dataset.jsFilterReset;
    const rootAccordion = document.querySelector(`#${key}`);

    const collapseEls = Array.from(rootAccordion.querySelectorAll('.accordion-collapse[id]'));

    e.target.data = {
      input: document.querySelector(`[data-js-filter-search="${key}"]`),
      rootAccordion,
      collapseEls,
      initialShown: new Set(collapseEls.filter((el) => el.classList.contains('show')).map((el) => el.id)),
      items: Array.from(rootAccordion.querySelectorAll('.accordion-item')),
      bsParentMap: new Map(),
      parentDisabled: false,
    };

    // Index for highlighting
    e.target.data.fuseData = e.target.data.items
      .map((itemEl) => {
        const labelEl = itemEl.querySelector(':scope > .accordion-header .accordion-button')
          || itemEl.querySelector(':scope > a');
        if (!labelEl) return null;
        const label = labelEl.textContent.trim();
        const labelHtml = labelEl.innerHTML.replace(/<\/?mark\b[^>]*>/g, '');
        return {
          label,
          labelSpan: labelEl,
          labelHtml,
          element: itemEl,
        };
      })
      .filter(Boolean);
  }

  e.target.data.input.value = '';

  // show everything
  e.target.data.items.forEach((itemEl) => {
    itemEl.style.display = '';
  });

  // clear highlighting
  e.target.data.fuseData.forEach((item) => {
    item.labelSpan.innerHTML = item.labelHtml;
  });

  restoreAccordionParent(e.target.data);

  // close all collapses on reset
  e.target.data.collapseEls.forEach((el) => {
    const inst = window.bootstrap.Collapse.getOrCreateInstance(el, { toggle: false });
    inst.hide();
  });
}

export function setupFilterSearch(e) {
  if (!e.target.data) {
    const key = e.target.dataset.jsFilterSearch;
    const rootAccordion = document.querySelector(`#${key}`);

    const items = Array.from(rootAccordion.querySelectorAll('.accordion-item'));
    const fuseData = items
      .map((itemEl) => {
        const labelEl = itemEl.querySelector(':scope > .accordion-header .accordion-button')
          || itemEl.querySelector(':scope > a');
        if (!labelEl) return null;
        const label = labelEl.textContent.trim();
        const labelHtml = labelEl.innerHTML.replace(/<\/?mark\b[^>]*>/g, '');
        return {
          label,
          labelSpan: labelEl,
          labelHtml,
          element: itemEl,
        };
      })
      .filter(Boolean);

    const collapseEls = Array.from(rootAccordion.querySelectorAll('.accordion-collapse[id]'));

    e.target.data = {
      key,
      rootAccordion,
      items,
      fuseData,
      fuse: new Fuse(fuseData, {
        keys: ['label'],
        threshold: 0,
        minMatchCharLength: 3,
        ignoreLocation: true,
        includeMatches: true,
      }),
      collapseEls,
      // to restore state when query clears
      initialShown: new Set(collapseEls.filter((el) => el.classList.contains('show')).map((el) => el.id)),
      bsParentMap: new Map(),
      parentDisabled: false,
    };
  }

  const query = e.target.value.trim();

  // Jeśli puste lub krótsze niż 3 znaki, pokaż wszystko
  if (query.length < 3) {
    restoreAccordionParent(e.target.data);

    e.target.data.items.forEach((itemEl) => {
      itemEl.style.display = '';
    });

    e.target.data.fuseData.forEach((item) => {
      item.labelSpan.innerHTML = item.labelHtml;
    });

    e.target.data.collapseEls.forEach((el) => {
      const inst = window.bootstrap.Collapse.getOrCreateInstance(el, { toggle: false });
      if (e.target.data.initialShown.has(el.id)) {
        inst.show();
      } else {
        inst.hide();
      }
    });

    return;
  }

  let results = e.target.data.fuse.search(query);

  // Opcja B: akceptuj tylko dopasowania ciągłe (substring), bez "pojedynczych liter" z przerwami
  const qLower = query.toLowerCase();
  results = results.filter((r) => r.item.label.toLowerCase().includes(qLower));

  const matchedItems = new Set(results.map((r) => r.item.element));

  // Compute which accordion-items must remain visible (matches + their ancestors)
  const visibleItems = new Set();
  for (const el of matchedItems) {
    let cur = el;
    while (cur && cur !== e.target.data.rootAccordion) {
      if (cur.classList && cur.classList.contains('accordion-item')) {
        visibleItems.add(cur);
      }
      cur = cur.parentElement;
    }
  }

  // Jeśli dopasowany jest rodzic (ma własny collapse), NIE ukrywaj jego dzieci.
  // Uwaga: to wpływa tylko na display (widoczność), NIE na otwieranie collapse.
  for (const matchedEl of matchedItems) {
    const ownCollapse = matchedEl.querySelector(':scope > .accordion-collapse');
    if (!ownCollapse) continue;

    const descendants = Array.from(ownCollapse.querySelectorAll('.accordion-item'));
    descendants.forEach((d) => visibleItems.add(d));
  }

  disableAccordionParent(e.target.data);

  // Determine which collapses must be open to reveal visible items
  // WAŻNE: NIE otwieramy collapse samego rodzica, jeśli tylko rodzic jest dopasowany.
  // Collapse otworzy się dopiero, gdy widoczny jest dopasowany potomek, bo wtedy potomek leży w tym collapse.
  const neededCollapses = new Set();

  for (const itemEl of matchedItems) {
    // Open ancestor collapses that contain THIS matched item
    let c = itemEl.closest('.accordion-collapse');
    while (c && c !== e.target.data.rootAccordion) {
      neededCollapses.add(c);
      c = c.parentElement?.closest?.('.accordion-collapse');
    }
  }

  // Hide/show items
  e.target.data.items.forEach((itemEl) => {
    itemEl.style.display = visibleItems.has(itemEl) ? '' : 'none';
  });

  // Open only the collapses needed for current results; close the rest
  e.target.data.collapseEls.forEach((el) => {
    const inst = window.bootstrap.Collapse.getOrCreateInstance(el, { toggle: false });
    if (neededCollapses.has(el)) {
      inst.show();
    } else {
      inst.hide();
    }
  });

  // Highlight (tylko ciągłe dopasowania przez <mark>)
  e.target.data.fuseData.forEach((item) => {
    if (!matchedItems.has(item.element)) {
      item.labelSpan.innerHTML = item.labelHtml;
      return;
    }

    item.labelSpan.innerHTML = highlightQuery(item.label, query);
  });
}
